import { useState, useEffect, useCallback } from 'react';
import { productosService } from '../services';
import { useDebounce } from './useDebounce';

/**
 * Hook personalizado para manejar operaciones CRUD de productos
 */
export const useProductos = () => {
    // Estados principales
    const [productos, setProductos] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Cambiar a true para carga inicial
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isInitialized, setIsInitialized] = useState(false); // Control de inicialización
    
    // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    /**
     * Transformar datos de la API al formato del frontend
     */
    const transformProductoFromAPI = useCallback((producto) => ({
        id: producto.id,
        nombreProducto: producto.name || '',
        descripcion: producto.description || '',
        codigo: producto.code || '',
        codigoBarras: producto.barcode || '',
        categoria: producto.category || '',
        departamento: producto.product_type || '',
        imagen: producto.image_url || null,
        precio: producto.sale_price || 0,
        precioDistribuidor: producto.wholesale_price || 0,
        precioMayorista: producto.wholesale_price || 0,
        costo: producto.cost_price || 0,
        stock: producto.current_stock || 0,
        stockMinimo: producto.min_stock || 0,
        stockMaximo: producto.max_stock || 0,
        activo: producto.status === 'active',
        fechaCreacion: producto.created_at || '',
        fechaActualizacion: producto.updated_at || '',
    }), []);

    /**
     * Cargar todos los productos (carga inicial)
     */
    const loadProductos = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const params = {
                skip: 0,
                limit: 100
            };

            const result = await productosService.getProductos(params);

            if (result.success) {
                const productosData = (result.data || []).map(transformProductoFromAPI);
                setProductos(productosData);
                setIsInitialized(true);
            } else {
                setError(result.message || 'Error al cargar los productos');
                setProductos([]);
            }
        } catch (err) {
            setError('Error de conexión al cargar los productos');
            console.error('Error loading productos:', err);
            setProductos([]);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Buscar productos (actualiza query)
     */
    const searchProductos = (query) => {
        setSearchQuery(query);
    };
    
    /**
     * Realizar búsqueda en la API
     */
    const performAPISearch = async (query) => {
        if (!query.trim()) {
            return; // No hacer nada si no hay query
        }

        setIsLoading(true);
        setError(null);
        
        try {
            const searchParams = {
                skip: 0,
                limit: 100,
                search: query
            };
            
            const result = await productosService.getProductos(searchParams);
            
            if (result.success) {
                const productosData = (result.data || []).map(transformProductoFromAPI);
                setProductos(productosData);
            } else {
                setError(result.message || 'Error en la búsqueda');
                setProductos([]);
            }
        } catch (err) {
            setError('Error de conexión en la búsqueda');
            console.error('Error searching productos:', err);
            setProductos([]);
        } finally {
            setIsLoading(false);
        }
    };
    
    /**
     * Recargar productos (forzar recarga)
     */
    const reloadProductos = () => {
        setSearchQuery(''); // Limpiar búsqueda
        setIsInitialized(false);
        loadProductos(); // Recargar productos originales
    };

    /**
     * Eliminar producto
     */
    const deleteProducto = async (productoId) => {
        try {
            const result = await productosService.deleteProducto(productoId);

            if (result.success) {
                const updatedProductos = productos.filter((p) => p.id !== productoId);
                setProductos(updatedProductos);

                return {
                    success: true,
                    message: 'Producto eliminado exitosamente',
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al eliminar el producto',
                };
            }
        } catch (err) {
            console.error('Error deleting producto:', err);
            return {
                success: false,
                error: 'Error inesperado al eliminar el producto',
            };
        }
    };

    /**
     * Obtener estadísticas de productos
     */
    const getProductosStats = () => ({
        total: productos.length,
        activos: productos.filter((p) => p.activo).length,
        stockBajo: productos.filter((p) => p.stock <= p.stockMinimo && p.stockMinimo > 0).length,
        sinStock: productos.filter((p) => p.stock === 0).length,
    });

    /**
     * Limpiar errores
     */
    const clearError = () => {
        setError(null);
    };

    // Efectos
    // Carga inicial (solo una vez al montar)
    useEffect(() => {
        loadProductos();
    }, []); // Dependencias vacías para ejecutar solo una vez

    // Efecto para búsqueda con debounce (independiente de la carga inicial)
    useEffect(() => {
        if (debouncedSearchQuery.trim()) {
            performAPISearch(debouncedSearchQuery);
        }
        // Si debouncedSearchQuery es vacío, no hacer nada (mantener productos actuales)
    }, [debouncedSearchQuery]); // Solo depende de debouncedSearchQuery

    return {
        // Datos
        productos,
        searchQuery,

        // Estados
        isLoading,
        error,

        // Funciones
        loadProductos,
        searchProductos,
        reloadProductos,
        deleteProducto,
        getProductosStats,
        clearError,
    };
};

/**
 * Hook para manejar un producto individual (crear/editar)
 */
export const useProducto = (productoId = null) => {
    // Estado del producto
    const [producto, setProducto] = useState({
        // Información básica
        name: '',
        description: '',
        code: '',
        barcode: '',
        imagen: null,

        // Clasificación
        product_type: 'BETEL',
        category: 'CAPSULES',

        // Precios
        cost_price: '',
        sale_price: '',
        wholesale_price: '',
        category_prices: [],

        // Portal de Cliente
        is_new: false,
        new_until: null,

        // Ofertas y Promociones
        is_on_sale: false,
        sale_discount_price: '',
        sale_starts: null,
        sale_ends: null,

        // Inventario
        current_stock: '',
        min_stock: '',
        max_stock: '',

        // Configuración
        unit: 'UNIT',
        weight: '',
        brand: '',
        track_inventory: true,
        expiration_days: '',
        notes: '',

        // Cliente NaturalAndLab
        naturalandlab_client_id: '',

        // Proveedores
        main_supplier_id: '',
        supplier_ids: [],

        // Formulación
        formulation_ingredients: null,

        // Estado
        status: 'ACTIVE',
    });

    // Estados de control
    const [originalProducto, setOriginalProducto] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const isEditing = Boolean(productoId);

    /**
     * Transformar datos de la API al formato del formulario
     */
    const transformProductoForForm = (data) => ({
        id: data.id,
        name: data.name || '',
        description: data.description || '',
        code: data.code || '',
        barcode: data.barcode || '',
        imagen: data.image_url || null,
        product_type: data.product_type || '',
        category: data.category || '',
        cost_price: data.cost_price || '',
        sale_price: data.sale_price || '',
        wholesale_price: data.wholesale_price || '',
        // Precios por categoría de cliente
        category_prices: data.category_prices || [],
        // Portal de Cliente
        is_new: data.is_new || false,
        new_until: data.new_until || null,
        // Ofertas y Promociones
        is_on_sale: data.is_on_sale || false,
        sale_discount_price: data.sale_discount_price || '',
        sale_starts: data.sale_starts || null,
        sale_ends: data.sale_ends || null,
        // Inventario
        current_stock: data.current_stock || '',
        min_stock: data.min_stock || '',
        max_stock: data.max_stock || '',
        unit: data.unit || 'UNIT',
        weight: data.weight || '',
        brand: data.brand || '',
        track_inventory: data.track_inventory !== false,
        expiration_days: data.expiration_days || '',
        notes: data.notes || '',
        naturalandlab_client_id: data.naturalandlab_client_id || '',
        main_supplier_id: data.main_supplier_id || '',
        supplier_ids: (data.product_suppliers || [])
            .filter(sp => sp.is_active !== false)
            .map(sp => sp.supplier_id),
        status: data.status || 'ACTIVE',
    });

    /**
     * Preparar datos para envío a la API
     */
    const prepareDataForAPI = (formData) => {
        // Generar código del producto si no existe
        let code = formData.code?.trim() || '';
        if (!code) {
            const prefix = formData.category ? formData.category.substring(0, 3).toUpperCase() : 'PRD';
            const suffix = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
            code = `${prefix}-${suffix}`;
        }

        // Generar código de barras si no existe
        let barcode = formData.barcode?.trim() || null;
        if (!barcode) {
            const codigo = Math.floor(Math.random() * 900000000000) + 100000000000;
            barcode = codigo.toString();
        }

        // Calcular sale_price desde category_prices si no existe
        // Usamos el precio más alto porque representa el precio base de venta
        // y debe ser mayor al costo según la validación del backend
        let sale_price = parseFloat(formData.sale_price) || 0;
        if (sale_price === 0 && formData.category_prices && formData.category_prices.length > 0) {
            const preciosValidos = formData.category_prices
                .filter(cp => cp.price && parseFloat(cp.price) > 0)
                .map(cp => parseFloat(cp.price));

            if (preciosValidos.length > 0) {
                // Usar el precio más alto como sale_price base
                sale_price = Math.max(...preciosValidos);
            }
        }

        const apiData = {
            name: formData.name?.trim() || '',
            description: formData.description ?.trim() || null,
            code: code,
            barcode: barcode,
            product_type: formData.product_type || '',
            category: formData.category || '',
            status: formData.status || 'ACTIVE',
            cost_price: parseFloat(formData.cost_price) || 0,
            sale_price: sale_price,
            wholesale_price: parseFloat(formData.wholesale_price) || 0,
            // Precios por categoría de cliente
            category_prices: formData.category_prices || [],
            // Portal de Cliente
            is_new: formData.is_new || false,
            new_until: formData.new_until || null,
            // Ofertas y Promociones
            is_on_sale: formData.is_on_sale || false,
            sale_discount_price: formData.is_on_sale ? (parseFloat(formData.sale_discount_price) || null) : null,
            sale_starts: formData.is_on_sale ? (formData.sale_starts || null) : null,
            sale_ends: formData.is_on_sale ? (formData.sale_ends || null) : null,
            // Inventario
            current_stock: parseFloat(formData.current_stock) || 0,
            min_stock: parseFloat(formData.min_stock) || 0,
            max_stock: parseFloat(formData.max_stock) || null,
            unit: formData.unit || 'UNIT',
            weight: parseFloat(formData.weight) || null,
            brand: formData.brand?.trim() || null,
            track_inventory: formData.track_inventory !== false,
            expiration_days: parseInt(formData.expiration_days) || null,
            notes: formData.notes?.trim() || null,
            main_supplier_id: null,
            supplier_ids: (formData.supplier_ids || []).map(id => parseInt(id)).filter(Boolean),
            // Cliente NaturalAndLab (solo para productos NATURALANDLAB)
            naturalandlab_client_id: formData.naturalandlab_client_id ? parseInt(formData.naturalandlab_client_id) : null,
            // Ingredientes de formulación (opcional)
            formulation_ingredients: formData.formulation_ingredients || null,
            // Metadatos de formulación (opcional)
            formula_data: formData.formula_data || null,
        };

        // Limpiar campos vacíos
        const fieldsToCleanAsNull = ['max_stock', 'weight', 'expiration_days', 'description', 'barcode', 'brand', 'notes', 'new_until', 'sale_discount_price', 'sale_starts', 'sale_ends', 'naturalandlab_client_id'];
        const fieldsToDelete = ['name', 'code', 'product_type', 'category'];

        Object.keys(apiData).forEach((key) => {
            if (apiData[key] === '' || apiData[key] === undefined) {
                if (fieldsToCleanAsNull.includes(key)) {
                    apiData[key] = null;
                } else if (fieldsToDelete.includes(key)) {
                    delete apiData[key];
                }
            }
        });

        return apiData;
    };

    /**
     * Cargar datos del producto (solo en modo edición)
     */
    const loadProducto = useCallback(async () => {
        if (!productoId) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await productosService.getProducto(productoId);

            if (result.success) {
                const productoData = transformProductoForForm(result.data);
                setProducto(productoData);
                setOriginalProducto({ ...productoData });
                setHasChanges(false);
            } else {
                setError(result.message || 'Error al cargar los datos del producto');
            }
        } catch (err) {
            setError('Error de conexión al cargar los datos del producto');
            console.error('Error loading producto:', err);
        } finally {
            setIsLoading(false);
        }
    }, [productoId]);

    /**
     * Actualizar datos del producto
     */
    const updateProductoData = useCallback(
        (field, value) => {
            setProducto((prev) => {
                const updated = { ...prev,
                    [field]: value
                };

                // Verificar si hay cambios comparando con el original
                const hasChanges = JSON.stringify(updated) !== JSON.stringify(originalProducto);
                setHasChanges(hasChanges);

                return updated;
            });
        }, [originalProducto]
    );

    /**
     * Guardar producto (crear o actualizar)
     * @param {Object} overrideData - Datos opcionales para sobrescribir campos del producto
     */
    const saveProducto = useCallback(async (overrideData = {}) => {
        try {
            // Combinar producto con datos de override
            const productoFinal = { ...producto, ...overrideData };
            const apiData = prepareDataForAPI(productoFinal);

            console.log('Datos a enviar a la API:', apiData);
            console.log('💰 Category prices en payload:', apiData.category_prices);
            console.log('🧪 Formulation ingredients en payload:', apiData.formulation_ingredients);

            let result;

            if (isEditing) {
                result = await productosService.updateProducto(productoId, apiData);
            } else {
                result = await productosService.createProducto(apiData);
            }

            if (result.success) {
                // Actualizar el estado del producto y original con datos del servidor
                const savedData = transformProductoForForm(result.data || producto);
                setProducto(savedData);
                setOriginalProducto(savedData);
                setHasChanges(false);

                return {
                    success: true,
                    message: result.message || `Producto ${producto.name} ${isEditing ? 'actualizado' : 'creado'} exitosamente`,
                    data: result.data,
                };
            } else {
                return {
                    success: false,
                    error: result.message || 'Error al guardar el producto',
                    detail: result.detail || null, // Incluir detail para traducción personalizada
                    validation_errors: result.validation_errors || [],
                    validationErrors: result.validationErrors || {},
                };
            }
        } catch (err) {
            console.error('Error saving producto:', err);
            return {
                success: false,
                error: 'Error de conexión al guardar el producto',
            };
        }
    }, [producto, isEditing, productoId]);

    /**
     * Resetear formulario
     */
    const resetProducto = useCallback(() => {
        const initialState = {
            name: '',
            description: '',
            code: '',
            barcode: '',
            imagen: null,
            product_type: 'BETEL',
            category: 'CAPSULES',
            cost_price: '',
            sale_price: '',
            wholesale_price: '',
            category_prices: [],
            // Portal de Cliente
            is_new: false,
            new_until: null,
            // Ofertas y Promociones
            is_on_sale: false,
            sale_discount_price: '',
            sale_starts: null,
            sale_ends: null,
            // Inventario
            current_stock: '',
            min_stock: '',
            max_stock: '',
            unit: 'UNIT',
            weight: '',
            brand: '',
            track_inventory: true,
            expiration_days: '',
            notes: '',
            naturalandlab_client_id: '',
            main_supplier_id: '',
            supplier_ids: [],
            formulation_ingredients: null,
            status: 'ACTIVE',
        };

        if (isEditing) {
            setProducto({ ...originalProducto });
        } else {
            setProducto(initialState);
            setOriginalProducto(initialState);
        }

        setHasChanges(false);
    }, [isEditing, originalProducto]);

    /**
     * Limpiar errores
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Efectos
    useEffect(() => {
        if (isEditing) {
            loadProducto();
        } else {
            // Inicializar producto nuevo
            setHasChanges(false);
        }
    }, [isEditing, loadProducto]);



    return {
        // Datos
        producto,
        originalProducto,

        // Estados
        isEditing,
        isLoading,
        error,
        hasChanges,

        // Funciones
        updateProductoData,
        saveProducto,
        resetProducto,
        clearError,
        loadProducto,
    };
};